import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useCart } from '@/features/cart/context/CartContext';
import { ShoppingBag, ChevronRight, PackageSearch } from 'lucide-react';
import '../customer.css';

const fmt = (n) => new Intl.NumberFormat('fa-IR').format(Number(n) || 0);

export default function ContractBasketPage() {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const [contractInfo, setContractInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const { data, error } = await apiClient.GET(`/api/v1/customer/profile/my-companies/${contractId}/items`);
                if (error) throw new Error(error.detail || 'خطا در دریافت اقلام');
                setContractInfo(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchItems();
    }, [contractId]);

    const handleAddToCart = async () => {
        if (!contractInfo || !contractInfo.items) return;
        setAdding(true);
        try {
            const isDiscount = contractInfo.contract_type === 'discount';
            const discountPercent = Number(contractInfo.discount_percent) || 0;

            for (const item of contractInfo.items) {
                const qty = item.max_qty || 1;
                const productObj = {
                    id: item.product_id,
                    name_fa: item.product_name,
                    base_price: item.base_price,
                    stock_quantity: 9999,
                    discount_percent: isDiscount ? discountPercent : 0,
                    package_name: isDiscount ? `قرارداد ${contractInfo.company_code}` : null
                };
                await addToCart(productObj, qty);
            }

            if (contractInfo.company_code) {
                sessionStorage.setItem('auto_company_info', JSON.stringify({
                    company_code: contractInfo.company_code,
                    identification_value: contractInfo.identification_value || ''
                }));
            }

            alert('اقلام به سبد خرید اضافه شدند. برای پرداخت به صفحه سبد خرید مراجعه کنید.');
        } catch (e) {
            alert('خطا در افزودن به سبد خرید: ' + e.message);
        } finally {
            setAdding(false);
        }
    };

    if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>در حال دریافت اقلام...</div>;
    if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>{error}</div>;
    if (!contractInfo) return null;

    const discountPercent = Number(contractInfo.discount_percent) || 0;
    const isDiscount = contractInfo.contract_type === 'discount';

    let grandTotalBase = 0;
    let grandTotalFinal = 0;

    contractInfo.items.forEach(item => {
        const qty = item.max_qty || 1;
        const base = item.base_price * qty;
        grandTotalBase += base;
        if (isDiscount) {
            grandTotalFinal += base - (base * (discountPercent / 100));
        } else {
            grandTotalFinal += base;
        }
    });

    return (
        <div className="customer-profile-page customer-contract-basket-page" style={{ padding: '2rem' }}>
            <div className="customer-profile-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <button className="customer-contract-basket-back" onClick={() => navigate('/profile/companies')} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', padding: 0 }}>
                        <ChevronRight size={18} /> بازگشت به شرکت‌ها
                    </button>
                    <h1 className="customer-profile-title">اقلام قرارداد</h1>
                    <p className="customer-profile-subtitle">محصولات موجود در این بسته را بررسی و به سبد خرید اضافه کنید</p>
                </div>
            </div>

            {contractInfo.items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', background: '#f8fafc', borderRadius: '12px' }}>
                    <PackageSearch size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                    <p style={{ color: '#64748b' }}>هیچ کالایی در این قرارداد ثبت نشده است.</p>
                </div>
            ) : (
                <div className="customer-contract-basket-table-wrap" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <table className="customer-contract-basket-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <tr>
                            <th style={{ padding: '1rem', textAlign: 'right' }}>کالا</th>
                            <th style={{ padding: '1rem', textAlign: 'center' }}>قیمت پایه</th>
                            <th style={{ padding: '1rem', textAlign: 'center' }}>تعداد</th>
                            <th style={{ padding: '1rem', textAlign: 'center' }}>جمع جزء</th>
                            {isDiscount && <th style={{ padding: '1rem', textAlign: 'center', color: '#10b981' }}>با تخفیف</th>}
                        </tr>
                        </thead>
                        <tbody>
                        {contractInfo.items.map((item) => {
                            const qty = item.max_qty || 1;
                            const totalBase = item.base_price * qty;
                            const totalDiscount = isDiscount ? (totalBase * (discountPercent / 100)) : 0;
                            const totalFinal = totalBase - totalDiscount;

                            return (
                                <tr key={item.product_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                            {item.image_url ? (
                                                <img src={item.image_url} alt={item.product_name} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <ShoppingBag size={20} color="#94a3b8" />
                                                </div>
                                            )}
                                            <span style={{ fontWeight: 'bold' }}>{item.product_name}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>{fmt(item.base_price)} ریال</td>
                                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>{qty}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#475569' }}>{fmt(totalBase)} ریال</td>
                                    {isDiscount && (
                                        <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#10b981' }}>
                                            {fmt(totalFinal)} ریال
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        </tbody>
                        <tfoot style={{ background: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                        <tr>
                            <td colSpan={isDiscount ? 3 : 2} style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', fontSize: '1rem' }}>
                                جمع کل:
                            </td>
                            <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#475569', fontSize: '1rem' }}>
                                {fmt(grandTotalBase)} ریال
                            </td>
                            {isDiscount && (
                                <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#10b981', fontSize: '1.1rem' }}>
                                    {fmt(grandTotalFinal)} ریال
                                </td>
                            )}
                        </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-start' }}>
                {/* === [FIXED] اضافه کردن width: fit-content برای خنثی کردن استایل سراسری === */}
                <button
                    onClick={handleAddToCart}
                    disabled={adding || contractInfo.items.length === 0}
                    className="btn-primary customer-contract-basket-add"
                    style={{
                        padding: '0.5rem 1.2rem',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        width: 'fit-content', // این خط باعث می‌شود دکمه به اندازه محتوایش فضا بگیرد
                        margin: 0
                    }}
                >
                    <ShoppingBag size={16} /> {adding ? 'در حال افزودن...' : 'افزودن به سبد خرید'}
                </button>
            </div>
        </div>
    );
}